import { PermissionControl } from '../../permissions/access';
import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Space, Tabs, Table, InputNumber } from 'antd';
import { getPlanConfig, updatePlanConfig, getCreditsPlanConfig, updateCreditsPlanConfig, getOrgSubscriptionPlanConfig, updateOrgSubscriptionPlanConfig, getOrgCreditsPlanConfig, updateOrgCreditsPlanConfig } from '../../api/system';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const SubscriptionScreen = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [planConfig, setPlanConfig] = useState(null);
    const [planJson, setPlanJson] = useState({ plans: [], features: [], prices: [] });
    const [initialPlanJson, setInitialPlanJson] = useState({ plans: [], features: [], prices: [] });

    // Credits Plan Config states
    const [creditsForm] = Form.useForm();
    const [creditsLoading, setCreditsLoading] = useState(false);
    const [creditsSubmitting, setCreditsSubmitting] = useState(false);
    const [creditsPlanConfig, setCreditsPlanConfig] = useState(null);
    const [creditsPlanJson, setCreditsPlanJson] = useState({ packages: [], initAmounts: [] });
    const [initialCreditsPlanJson, setInitialCreditsPlanJson] = useState({ packages: [], initAmounts: [] });

	// Organization Subscription Plan Config states
	const [orgSubForm] = Form.useForm();
	const [orgSubLoading, setOrgSubLoading] = useState(false);
	const [orgSubSubmitting, setOrgSubSubmitting] = useState(false);
	const [orgSubPlanConfig, setOrgSubPlanConfig] = useState(null);
	const [orgSubPlanJson, setOrgSubPlanJson] = useState({ prices: [] });
	const [initialOrgSubPlanJson, setInitialOrgSubPlanJson] = useState({ prices: [] });

	// Organization Credits Plan Config states
	const [orgCreditsForm] = Form.useForm();
	const [orgCreditsLoading, setOrgCreditsLoading] = useState(false);
	const [orgCreditsSubmitting, setOrgCreditsSubmitting] = useState(false);
	const [orgCreditsPlanConfig, setOrgCreditsPlanConfig] = useState(null);
	const [orgCreditsPlanJson, setOrgCreditsPlanJson] = useState({ packages: [], initAmounts: [] });
	const [initialOrgCreditsPlanJson, setInitialOrgCreditsPlanJson] = useState({ packages: [], initAmounts: [] });

    useEffect(() => {
        fetchPlanConfig();
        fetchCreditsPlanConfig();
        fetchOrgSubPlanConfig();
        fetchOrgCreditsPlanConfig();
    }, []);

    const fetchPlanConfig = () => {
        setLoading(true);
        getPlanConfig().then(res => {
            if (res) {
                setPlanConfig(res);
                form.setFieldsValue({
                    name: res.name,
                    titleHeadline: res.titleHeadline
                });
                try {
                    const parsed = JSON.parse(res.planJson);
                    setPlanJson(parsed);
                    setInitialPlanJson(JSON.parse(JSON.stringify(parsed))); // 深拷贝保存初始数据
                } catch (error) {
                    console.error('Failed to parse planJson:', error);
                    setPlanJson({ plans: [], features: [], prices: [] });
                    setInitialPlanJson({ plans: [], features: [], prices: [] });
                }
            }
        }).catch(err => {
            message.error('Failed to fetch plan config');
            console.error(err);
        }).finally(() => setLoading(false));
    };

    const handleSubmit = (values) => {
        setSubmitting(true);
        updatePlanConfig({
            name: values.name,
            titleHeadline: values.titleHeadline,
            planJson: JSON.stringify(planJson)
        }).then(res => {
            if (res) {
                message.success('Plan config updated successfully');
                setPlanConfig(res);
            }
        }).catch(err => {
            message.error('Failed to update plan config');
            console.error(err);
        }).finally(() => setSubmitting(false));
    };

    const fetchCreditsPlanConfig = () => {
        setCreditsLoading(true);
        getCreditsPlanConfig().then(res => {
            if (res) {
                setCreditsPlanConfig(res);
                creditsForm.setFieldsValue({ name: res.name });
                try {
                    const parsed = JSON.parse(res.planJson);
                    setCreditsPlanJson(parsed);
                    setInitialCreditsPlanJson(JSON.parse(JSON.stringify(parsed)));
                } catch (error) {
                    console.error('Failed to parse credits planJson:', error);
                    setCreditsPlanJson({ packages: [], initAmounts: [] });
                    setInitialCreditsPlanJson({ packages: [], initAmounts: [] });
                }
            }
        }).catch(err => {
            message.error('Failed to fetch credits plan config');
            console.error(err);
        }).finally(() => setCreditsLoading(false));
    };

    const handleCreditsSubmit = (values) => {
        setCreditsSubmitting(true);
        updateCreditsPlanConfig({
            name: values.name,
            planJson: JSON.stringify(creditsPlanJson)
        }).then(res => {
            message.success('Credits plan config updated successfully');
            fetchCreditsPlanConfig();
        }).catch(err => {
            message.error('Failed to update credits plan config');
            console.error(err);
        }).finally(() => setCreditsSubmitting(false));
    };


    const fetchOrgSubPlanConfig = () => {
        setOrgSubLoading(true);
        getOrgSubscriptionPlanConfig().then(res => {
            if (res) {
                setOrgSubPlanConfig(res);
                orgSubForm.setFieldsValue({
                    name: res.name,
                    titleHeadline: res.titleHeadline,
                    companyName: res.companyName,
                    companyAddress: res.companyAddress,
                    bankName: res.bankName,
                    accountNumber: res.accountNumber,
                    swiftCode: res.swiftCode
                });
                try {
                    const parsed = JSON.parse(res.planJson);
                    setOrgSubPlanJson(parsed);
                    setInitialOrgSubPlanJson(JSON.parse(JSON.stringify(parsed)));
                } catch (error) {
                    console.error('Failed to parse org subscription planJson:', error);
                    setOrgSubPlanJson({ prices: [] });
                    setInitialOrgSubPlanJson({ prices: [] });
                }
            }
        }).catch(err => {
            message.error('Failed to fetch organization subscription plan config');
            console.error(err);
        }).finally(() => setOrgSubLoading(false));
    };

    const handleOrgSubSubmit = (values) => {
        setOrgSubSubmitting(true);
        updateOrgSubscriptionPlanConfig({
            name: values.name,
            titleHeadline: values.titleHeadline,
            planJson: JSON.stringify(orgSubPlanJson),
            companyName: values.companyName,
            companyAddress: values.companyAddress,
            bankName: values.bankName,
            accountNumber: values.accountNumber,
            swiftCode: values.swiftCode
        }).then(res => {
            message.success('Organization subscription plan config updated successfully');
            fetchOrgSubPlanConfig();
        }).catch(err => {
            message.error('Failed to update organization subscription plan config');
            console.error(err);
        }).finally(() => setOrgSubSubmitting(false));
    };

    const fetchOrgCreditsPlanConfig = () => {
        setOrgCreditsLoading(true);
        getOrgCreditsPlanConfig().then(res => {
            if (res) {
                setOrgCreditsPlanConfig(res);
                orgCreditsForm.setFieldsValue({ name: res.name });
                try {
                    const parsed = JSON.parse(res.planJson);
                    setOrgCreditsPlanJson(parsed);
                    setInitialOrgCreditsPlanJson(JSON.parse(JSON.stringify(parsed)));
                } catch (error) {
                    console.error('Failed to parse org credits planJson:', error);
                    setOrgCreditsPlanJson({ packages: [], initAmounts: [] });
                    setInitialOrgCreditsPlanJson({ packages: [], initAmounts: [] });
                }
            }
        }).catch(err => {
            message.error('Failed to fetch organization credits plan config');
            console.error(err);
        }).finally(() => setOrgCreditsLoading(false));
    };

    const handleOrgCreditsSubmit = (values) => {
        setOrgCreditsSubmitting(true);
        updateOrgCreditsPlanConfig({
            name: values.name,
            planJson: JSON.stringify(orgCreditsPlanJson)
        }).then(res => {
            message.success('Organization credits plan config updated successfully');
            fetchOrgCreditsPlanConfig();
        }).catch(err => {
            message.error('Failed to update organization credits plan config');
            console.error(err);
        }).finally(() => setOrgCreditsSubmitting(false));
    };
    const creditsPackagesColumns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 120,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].key = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].name = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Credits',
            dataIndex: 'credits',
            key: 'credits',
            width: 100,
            render: (text, record, index) => (
                <InputNumber value={text} onChange={(value) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].credits = value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Stripe Price ID',
            dataIndex: 'stripePriceId',
            key: 'stripePriceId',
            width: 200,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].stripePriceId = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Apple Product ID',
            dataIndex: 'appleProductId',
            key: 'appleProductId',
            width: 140,
            render: (text, record, index) => (
                <Input value={text || ''} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].appleProductId = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Google Product ID',
            dataIndex: 'googleProductId',
            key: 'googleProductId',
            width: 140,
            render: (text, record, index) => (
                <Input value={text || ''} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].googleProductId = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Google Purchase Option ID',
            dataIndex: 'googlePurchaseOptionId',
            key: 'googlePurchaseOptionId',
            width: 180,
            render: (text, record, index) => (
                <Input value={text || ''} onChange={(e) => {
                    const arr = [...creditsPlanJson.packages];
                    arr[index].googlePurchaseOptionId = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 60,
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.9df4b94e"><Button type='link' danger size='small' icon={<DeleteOutlined />}
                    onClick={() => {
                        const arr = creditsPlanJson.packages.filter((_, i) => i !== index);
                        setCreditsPlanJson({ ...creditsPlanJson, packages: arr });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];

    const creditsInitAmountsColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...creditsPlanJson.initAmounts];
                    arr[index].name = e.target.value;
                    setCreditsPlanJson({ ...creditsPlanJson, initAmounts: arr });
                }} />
            )
        },
        {
            title: 'Credits',
            dataIndex: 'credits',
            key: 'credits',
            render: (text, record, index) => (
                <InputNumber value={text} onChange={(value) => {
                    const arr = [...creditsPlanJson.initAmounts];
                    arr[index].credits = value;
                    setCreditsPlanJson({ ...creditsPlanJson, initAmounts: arr });
                }} />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.a4118f06"><Button type='link' danger size='small' icon={<DeleteOutlined />}
                    onClick={() => {
                        const arr = creditsPlanJson.initAmounts.filter((_, i) => i !== index);
                        setCreditsPlanJson({ ...creditsPlanJson, initAmounts: arr });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];



    // Organization subscription prices columns (matches DB: prices[].seats.base.count + seats.per_seat.stripe_price_id)
    const orgSubPricesColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...orgSubPlanJson.prices];
                        newPrices[index].name = e.target.value;
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 80,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...orgSubPlanJson.prices];
                        newPrices[index].key = e.target.value;
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Months',
            dataIndex: 'months',
            key: 'months',
            width: 70,
            render: (text, record, index) => (
                <InputNumber
                    value={text}
                    onChange={(value) => {
                        const newPrices = [...orgSubPlanJson.prices];
                        newPrices[index].months = value;
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Base Seats',
            key: 'baseCount',
            width: 80,
            render: (_, record, index) => (
                <InputNumber
                    value={record.seats?.base?.count}
                    min={1}
                    onChange={(value) => {
                        const newPrices = [...orgSubPlanJson.prices];
                        if (!newPrices[index].seats) newPrices[index].seats = {};
                        if (!newPrices[index].seats.base) newPrices[index].seats.base = {};
                        newPrices[index].seats.base.count = value;
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Per Seat Stripe Price ID',
            key: 'perSeatPriceId',
            width: 240,
            render: (_, record, index) => (
                <Input
                    value={record.seats?.per_seat?.stripe_price_id || record.seats?.per_seat?.stripePriceId || ''}
                    onChange={(e) => {
                        const newPrices = [...orgSubPlanJson.prices];
                        if (!newPrices[index].seats) newPrices[index].seats = {};
                        if (!newPrices[index].seats.per_seat) newPrices[index].seats.per_seat = {};
                        newPrices[index].seats.per_seat.stripe_price_id = e.target.value;
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 60,
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.a5b42eb2"><Button
                    type='link'
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        const newPrices = orgSubPlanJson.prices.filter((_, i) => i !== index);
                        setOrgSubPlanJson({ ...orgSubPlanJson, prices: newPrices });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];

    // Organization credits packages columns
    const orgCreditsPackagesColumns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 120,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...orgCreditsPlanJson.packages];
                    arr[index].key = e.target.value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...orgCreditsPlanJson.packages];
                    arr[index].name = e.target.value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Credits',
            dataIndex: 'credits',
            key: 'credits',
            width: 100,
            render: (text, record, index) => (
                <InputNumber value={text} onChange={(value) => {
                    const arr = [...orgCreditsPlanJson.packages];
                    arr[index].credits = value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Stripe Price ID',
            dataIndex: 'stripePriceId',
            key: 'stripePriceId',
            width: 200,
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...orgCreditsPlanJson.packages];
                    arr[index].stripePriceId = e.target.value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, packages: arr });
                }} />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 60,
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.f9193d81"><Button type='link' danger size='small' icon={<DeleteOutlined />}
                    onClick={() => {
                        const arr = orgCreditsPlanJson.packages.filter((_, i) => i !== index);
                        setOrgCreditsPlanJson({ ...orgCreditsPlanJson, packages: arr });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];

    // Organization credits init amounts columns
    const orgCreditsInitAmountsColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => (
                <Input value={text} onChange={(e) => {
                    const arr = [...orgCreditsPlanJson.initAmounts];
                    arr[index].name = e.target.value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, initAmounts: arr });
                }} />
            )
        },
        {
            title: 'Credits',
            dataIndex: 'credits',
            key: 'credits',
            render: (text, record, index) => (
                <InputNumber value={text} onChange={(value) => {
                    const arr = [...orgCreditsPlanJson.initAmounts];
                    arr[index].credits = value;
                    setOrgCreditsPlanJson({ ...orgCreditsPlanJson, initAmounts: arr });
                }} />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.f0a04415"><Button type='link' danger size='small' icon={<DeleteOutlined />}
                    onClick={() => {
                        const arr = orgCreditsPlanJson.initAmounts.filter((_, i) => i !== index);
                        setOrgCreditsPlanJson({ ...orgCreditsPlanJson, initAmounts: arr });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];
    const plansColumns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPlans = [...planJson.plans];
                        newPlans[index].key = e.target.value;
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPlans = [...planJson.plans];
                        newPlans[index].name = e.target.value;
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.f0fe4305"><Button
                    type='link'
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        const newPlans = planJson.plans.filter((_, i) => i !== index);
                        setPlanJson({ ...planJson, plans: newPlans });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];

    const pricesColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].name = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: 100,
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].key = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Months',
            dataIndex: 'months',
            key: 'months',
            render: (text, record, index) => (
                <InputNumber
                    value={text}
                    onChange={(value) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].months = value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Stripe Price ID',
            dataIndex: 'stripePriceId',
            key: 'stripePriceId',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].stripePriceId = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Apple Product ID',
            dataIndex: 'appleProductId',
            key: 'appleProductId',
            render: (text, record, index) => (
                <Input
                    value={text || ''}
                    onChange={(e) => {
                        const newPrices = [...planJson.prices];
                        newPrices[index].appleProductId = e.target.value;
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                />
            )
        },
        {
            title: 'Google Product ID / Plan ID',
            key: 'googleIds',
            render: (_, record, index) => (
                <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                    <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Product ID:</label>
                        <Input
                            value={record.googleProductId || ''}
                            onChange={(e) => {
                                const newPrices = [...planJson.prices];
                                newPrices[index].googleProductId = e.target.value;
                                setPlanJson({ ...planJson, prices: newPrices });
                            }}
                            size='small'
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, color: '#666' }}>Plan ID:</label>
                        <Input
                            value={record.googlePlanId || ''}
                            onChange={(e) => {
                                const newPrices = [...planJson.prices];
                                newPrices[index].googlePlanId = e.target.value;
                                setPlanJson({ ...planJson, prices: newPrices });
                            }}
                            size='small'
                        />
                    </div>
                </div>
            )
        },
        {
            title: 'Tips (JSON)',
            dataIndex: 'tips',
            key: 'tips',
            render: (text, record, index) => (
                <Input.TextArea
                    value={Array.isArray(text) ? JSON.stringify(text, null, 2) : '[]'}
                    onChange={(e) => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            const newPrices = [...planJson.prices];
                            newPrices[index].tips = parsed;
                            setPlanJson({ ...planJson, prices: newPrices });
                        } catch (error) {
                            console.error('Invalid JSON');
                        }
                    }}
                    rows={3}
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record, index) => (
                <PermissionControl permission="subscription.control.filter.d630088e"><Button
                    type='link'
                    danger
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        const newPrices = planJson.prices.filter((_, i) => i !== index);
                        setPlanJson({ ...planJson, prices: newPrices });
                    }}
                 aria-label="filter"/></PermissionControl>
            )
        }
    ];

    return (
        <div className='main-wrapper'>
            <div className='cnt-item'>
                <Card title='Subscription Plan Config' loading={loading}>
                    <Spin spinning={loading}>
                        <Form
                            form={form}
                            layout='vertical'
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                name='name'
                                label='Plan Name'
                                rules={[{ required: true, message: 'Please enter plan name' }]}
                            >
                                <Input placeholder='Enter plan name' />
                            </Form.Item>

                            <Form.Item
                                name='titleHeadline'
                                label='Title Headline'
                                rules={[{ required: true, message: 'Please enter title headline' }]}
                            >
                                <Input placeholder='Enter title headline' />
                            </Form.Item>

                            <Card title='Plan JSON Details' style={{ marginTop: 24 }}>
                                <Tabs
                                    items={[
                                        {
                                            label: 'Plans',
                                            key: 'plans',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={plansColumns}
                                                        dataSource={planJson.plans}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                    />
                                                    <PermissionControl permission="subscription.control.setPlanJson.5be9a7a8"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setPlanJson({
                                                                ...planJson,
                                                                plans: [...planJson.plans, { key: '', name: '' }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Plan
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        },
                                        {
                                            label: 'Prices',
                                            key: 'prices',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={pricesColumns}
                                                        dataSource={planJson.prices}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                        scroll={{ x: 1200 }}
                                                    />
                                                    <PermissionControl permission="subscription.control.setPlanJson.c8a7ab0c"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setPlanJson({
                                                                ...planJson,
                                                                prices: [...planJson.prices, { name: '', key: '', months: 1, stripePriceId: '', tips: [] }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Price
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <PermissionControl permission="subscription.control.button.4c9ca5ef"><Button type='primary' htmlType='submit' loading={submitting}>
                                        Save
                                    </Button></PermissionControl>
                                    <PermissionControl permission="subscription.control.setFieldsValue.2cc25840"><Button onClick={() => {
                                        form.setFieldsValue({
                                            name: planConfig?.name,
                                            titleHeadline: planConfig?.titleHeadline
                                        });
                                        setPlanJson(JSON.parse(JSON.stringify(initialPlanJson)));
                                    }}>
                                        Reset
                                    </Button></PermissionControl>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>
            </div>
            <div className='cnt-item' style={{ marginTop: 24 }}>
                <Card title='Credits Plan Config' loading={creditsLoading}>
                    <Spin spinning={creditsLoading}>
                        <Form
                            form={creditsForm}
                            layout='vertical'
                            onFinish={handleCreditsSubmit}
                        >
                            <Form.Item
                                name='name'
                                label='Config Name'
                                rules={[{ required: true, message: 'Please enter config name' }]}
                            >
                                <Input placeholder='Enter config name' />
                            </Form.Item>

                            <Card title='Plan JSON Details' style={{ marginTop: 24 }}>
                                <Tabs
                                    items={[
                                        {
                                            label: 'Packages',
                                            key: 'packages',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={creditsPackagesColumns}
                                                        dataSource={creditsPlanJson.packages}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                        scroll={{ x: 1200 }}
                                                    />
                                                    <PermissionControl permission="subscription.control.setCreditsPlanJson.569b6301"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setCreditsPlanJson({
                                                                ...creditsPlanJson,
                                                                packages: [...creditsPlanJson.packages, { key: '', name: '', credits: 0, stripePriceId: '', appleProductId: '', googleProductId: '', googlePurchaseOptionId: '' }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Package
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        },
                                        {
                                            label: 'Init Amounts',
                                            key: 'initAmounts',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={creditsInitAmountsColumns}
                                                        dataSource={creditsPlanJson.initAmounts}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                    />
                                                    <PermissionControl permission="subscription.control.setCreditsPlanJson.d330d219"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setCreditsPlanJson({
                                                                ...creditsPlanJson,
                                                                initAmounts: [...creditsPlanJson.initAmounts, { name: '', credits: 0 }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Init Amount
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <PermissionControl permission="subscription.control.button.0c9d65e0"><Button type='primary' htmlType='submit' loading={creditsSubmitting}>
                                        Save
                                    </Button></PermissionControl>
                                    <PermissionControl permission="subscription.control.setFieldsValue.08273e12"><Button onClick={() => {
                                        creditsForm.setFieldsValue({ name: creditsPlanConfig?.name });
                                        setCreditsPlanJson(JSON.parse(JSON.stringify(initialCreditsPlanJson)));
                                    }}>
                                        Reset
                                    </Button></PermissionControl>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>
            </div>

            <div className='cnt-item' style={{ marginTop: 24 }}>
                <Card title='Team Subscription Plan Config' loading={orgSubLoading}>
                    <Spin spinning={orgSubLoading}>
                        <Form
                            form={orgSubForm}
                            layout='vertical'
                            onFinish={handleOrgSubSubmit}
                        >
                            <Form.Item
                                name='name'
                                label='Plan Name'
                                rules={[{ required: true, message: 'Please enter plan name' }]}
                            >
                                <Input placeholder='Enter plan name' />
                            </Form.Item>

                            <Form.Item
                                name='titleHeadline'
                                label='Title Headline'
                            >
                                <Input placeholder='Enter title headline' />
                            </Form.Item>

                            <Card title='Plan JSON Details' style={{ marginTop: 24 }}>
                                <Tabs
                                    items={[
                                        {
                                            label: 'Prices',
                                            key: 'prices',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={orgSubPricesColumns}
                                                        dataSource={orgSubPlanJson.prices}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                        scroll={{ x: 900 }}
                                                    />
                                                    <PermissionControl permission="subscription.control.setOrgSubPlanJson.ba71de00"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setOrgSubPlanJson({
                                                                ...orgSubPlanJson,
                                                                prices: [...orgSubPlanJson.prices, { name: '', key: '', months: 1, seats: { base: { count: 5 }, per_seat: { count: 1, stripe_price_id: '' } } }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Price
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            <Card title='Offline Bank Account' style={{ marginTop: 24 }}>
                                <Form.Item
                                    name='companyName'
                                    label='Beneficiary'
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter beneficiary' },
                                        { max: 255, message: 'Beneficiary must not exceed 255 characters' }
                                    ]}
                                >
                                    <Input placeholder='Enter beneficiary name' />
                                </Form.Item>

                                <Form.Item
                                    name='companyAddress'
                                    label='Company Address'
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter company address' },
                                        { max: 500, message: 'Company address must not exceed 500 characters' }
                                    ]}
                                >
                                    <Input.TextArea rows={3} placeholder='Enter company address' />
                                </Form.Item>

                                <Form.Item
                                    name='bankName'
                                    label='Bank Name'
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter bank name' },
                                        { max: 255, message: 'Bank name must not exceed 255 characters' }
                                    ]}
                                >
                                    <Input placeholder='Enter bank name' />
                                </Form.Item>

                                <Form.Item
                                    name='accountNumber'
                                    label='Account Number'
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter account number' },
                                        { max: 128, message: 'Account number must not exceed 128 characters' }
                                    ]}
                                >
                                    <Input placeholder='Enter account number' />
                                </Form.Item>

                                <Form.Item
                                    name='swiftCode'
                                    label='SWIFT / BIC'
                                    rules={[
                                        { required: true, whitespace: true, message: 'Please enter SWIFT / BIC' },
                                        { max: 64, message: 'SWIFT / BIC must not exceed 64 characters' }
                                    ]}
                                >
                                    <Input placeholder='Enter SWIFT / BIC' />
                                </Form.Item>
                            </Card>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <PermissionControl permission="subscription.control.button.0ca72b16"><Button type='primary' htmlType='submit' loading={orgSubSubmitting}>
                                        Save
                                    </Button></PermissionControl>
                                    <PermissionControl permission="subscription.control.setFieldsValue.d6744052"><Button onClick={() => {
                                        orgSubForm.setFieldsValue({
                                            name: orgSubPlanConfig?.name,
                                            titleHeadline: orgSubPlanConfig?.titleHeadline,
                                            companyName: orgSubPlanConfig?.companyName,
                                            companyAddress: orgSubPlanConfig?.companyAddress,
                                            bankName: orgSubPlanConfig?.bankName,
                                            accountNumber: orgSubPlanConfig?.accountNumber,
                                            swiftCode: orgSubPlanConfig?.swiftCode
                                        });
                                        setOrgSubPlanJson(JSON.parse(JSON.stringify(initialOrgSubPlanJson)));
                                    }}>
                                        Reset
                                    </Button></PermissionControl>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>
            </div>
            <div className='cnt-item' style={{ marginTop: 24 }}>
                <Card title='Team Credits Plan Config' loading={orgCreditsLoading}>
                    <Spin spinning={orgCreditsLoading}>
                        <Form
                            form={orgCreditsForm}
                            layout='vertical'
                            onFinish={handleOrgCreditsSubmit}
                        >
                            <Form.Item
                                name='name'
                                label='Config Name'
                                rules={[{ required: true, message: 'Please enter config name' }]}
                            >
                                <Input placeholder='Enter config name' />
                            </Form.Item>

                            <Card title='Plan JSON Details' style={{ marginTop: 24 }}>
                                <Tabs
                                    items={[
                                        {
                                            label: 'Packages',
                                            key: 'packages',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={orgCreditsPackagesColumns}
                                                        dataSource={orgCreditsPlanJson.packages}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                        scroll={{ x: 800 }}
                                                    />
                                                    <PermissionControl permission="subscription.control.setOrgCreditsPlanJson.92712b88"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setOrgCreditsPlanJson({
                                                                ...orgCreditsPlanJson,
                                                                packages: [...orgCreditsPlanJson.packages, { key: '', name: '', credits: 0, stripePriceId: '' }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Package
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        },
                                        {
                                            label: 'Init Amounts',
                                            key: 'initAmounts',
                                            children: (
                                                <div>
                                                    <Table
                                                        columns={orgCreditsInitAmountsColumns}
                                                        dataSource={orgCreditsPlanJson.initAmounts}
                                                        rowKey={(_, index) => index}
                                                        pagination={false}
                                                        size='small'
                                                    />
                                                    <PermissionControl permission="subscription.control.setOrgCreditsPlanJson.60de055e"><Button
                                                        type='dashed'
                                                        block
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            setOrgCreditsPlanJson({
                                                                ...orgCreditsPlanJson,
                                                                initAmounts: [...orgCreditsPlanJson.initAmounts, { name: '', credits: 0 }]
                                                            });
                                                        }}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        Add Init Amount
                                                    </Button></PermissionControl>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </Card>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <PermissionControl permission="subscription.control.button.b98bf607"><Button type='primary' htmlType='submit' loading={orgCreditsSubmitting}>
                                        Save
                                    </Button></PermissionControl>
                                    <PermissionControl permission="subscription.control.setFieldsValue.1906316d"><Button onClick={() => {
                                        orgCreditsForm.setFieldsValue({ name: orgCreditsPlanConfig?.name });
                                        setOrgCreditsPlanJson(JSON.parse(JSON.stringify(initialOrgCreditsPlanJson)));
                                    }}>
                                        Reset
                                    </Button></PermissionControl>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>
            </div>
        </div>
    );
};

export default SubscriptionScreen;
